/**
 * displayName.ts — privacy guard for player names shown in Puzzle Royale.
 *
 * WHY: a chunk of accounts have no real name set, so the backend falls back to
 * the signup MOBILE NUMBER as `user_name` / `name`. Puzzle Royale then renders
 * that raw in the tournament roster, the lobby, the live leaderboard and the
 * results — leaking one player's phone number to every other player in the
 * race (a stranger-visible list, unlike a classroom).
 *
 * WHAT: if the display name is *only* digits and long enough to be a phone
 * number, show the FIRST HALF and mask the rest — `9876543210` → `98765*****`.
 * Anything that isn't a bare number is returned untouched, so real names,
 * usernames-with-digits ("magnus2000") and "Player 42" fallbacks are unaffected.
 *
 * This is DISPLAY-ONLY. The stored/transmitted value is never rewritten: the
 * register envelope still sends the true name, so the server, results rows and
 * staff tooling keep the real identity.
 *
 * Mirrors the existing house convention in `@/Utils/CommonUtils`
 * (`maskPhoneNumber` + `isPhoneNumber`, used by `formatPlayerDisplay` for chess
 * tournaments) — same 10–15 digit threshold, same '*' fill. Kept as a local
 * royale util rather than exporting those internals so shared chess/tournament
 * behaviour is not disturbed.
 */

/** Digits-only and plausibly a mobile number (matches CommonUtils' rule). */
function isPhoneLike(digits: string): boolean {
  return /^\d{10,15}$/.test(digits);
}

/**
 * Mask a display name that is really a phone number.
 *
 * Shows the first half (rounded UP for odd lengths, so a 13-digit number keeps
 * 7 visible) and replaces the remainder with '*'.
 *
 * @param name raw display name; may be null/undefined/empty.
 * @returns the name unchanged, or the masked form when it's a bare number.
 */
export function maskIfPhoneNumber(
  name: string | null | undefined,
): string | null | undefined {
  if (!name || typeof name !== 'string') {
    return name;
  }
  const trimmed = name.trim();
  // Only a name that is ENTIRELY digits can be a leaked number. Bail out before
  // stripping punctuation so "Player 9876543210" or "A-1234567890" — which are
  // not bare numbers — are left alone rather than silently rewritten.
  if (!/^\d+$/.test(trimmed) || !isPhoneLike(trimmed)) {
    return name;
  }
  const visibleCount = Math.ceil(trimmed.length / 2);
  return (
    trimmed.slice(0, visibleCount) + '*'.repeat(trimmed.length - visibleCount)
  );
}

export default maskIfPhoneNumber;
